<template lang="pug">
.safety-confirm-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    // セル：日付
    template(#cell(created)="row") 
      span {{ convertDate(row.item.created) }}
      span （
      span(:class="getWeekCssClass(row.item.created)") {{ convertWeek(row.item.created) }}
      span ）
    // セル：施設関係者の安否確認
    template(#cell(otherUser)="row")
      // 新規作成
      .no-messages.text-center(v-if="row.item.otherUserMessagesSortKey == null")
        button.register-btn.btn(@click="onGotoSendOtherUserSafetyConfirm(row.item)") 登録・送信
      // 受付中
      .accepting.d-flex.flex-row.justify-content-center.align-items-center(v-else-if="row.item.acceptingOtherUserReply == true")
        .accepting-text.mr-2 受付中
        button.detail-btn.btn(@click="onOtherUserDetail(row.item)") 詳細
      // 受付停止
      .not-accepting.d-flex.flex-row.justify-content-center.align-items-center(v-else)
        .no-accepting-text.mr-2 受付停止
        button.detail-btn.btn(@click="onOtherUserDetail(row.item)") 詳細
    // セル：家族への情報提供
    template(#cell(family)="row")
      // 新規作成
      .no-messages.text-center(v-if="row.item.messagesSortKey == null || row.item.messagesSortKey.length < 1")
        button.register-btn.btn(@click="onFamilyDetail(row.item)") 登録・送信
      // 受付中
      .accepting.d-flex.flex-row.justify-content-center.align-items-center(v-else-if="row.item.acceptingFamilyReply == true")
        .accepting-text.mr-2 受付中
        button.detail-btn.btn(@click="onFamilyDetail(row.item)") 詳細
      // 受付停止
      .not-accepting.d-flex.flex-row.justify-content-center.align-items-center(v-else)
        .no-accepting-text.mr-2 受付停止
        button.detail-btn.btn(@click="onFamilyDetail(row.item)") 詳細
    // セル：操作
    template(#cell(control)="row")
      button.detail-btn.btn(@click="onDelete(row.item)") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

/**
 * ### 安否確認テーブルコンポーネント
 */
@Component({})
export default class SafetyConfirmsTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<SafetyConfirmsTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<SafetyConfirmsTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  get _value(): Array<SafetyConfirmsTableItem> {
    return this.value;
  }

  /**
   * ### 日付の変換
   */
  public convertDate(datetime: number): string {
    const date: string = this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日");

    return date;
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public getWeekCssClass(datetime: number): string {
    let weekClassName: string = "";
    const weekName: string = this.convertWeek(datetime);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * ### 曜日の変換
   */
  public convertWeek(datetime: number): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(datetime);
    const week: string = this.$dateUtil.getWeekName(dateObject, "ja");

    return week;
  }

  /**
   * ### 施設関係者への安否確認メッセージ送信画面へ遷移する
   */
  public onGotoSendOtherUserSafetyConfirm(item: SafetyConfirmsTableItem): void {
    this.$emit("onGotoSendOtherUserSafetyConfirm", item);

    return;
  }

  /**
   * ### 施設関係者への安否確認メッセージ詳細画面へ遷移する
   */
  public onOtherUserDetail(item: SafetyConfirmsTableItem): void {
    this.$emit("onOtherUserDetail", item);

    return;
  }

  /**
   * ### 家族への安否確認メッセージ詳細画面へ遷移する
   */
  public onFamilyDetail(item: SafetyConfirmsTableItem): void {
    this.$emit("onFamilyDetail", item);

    return;
  }

  public onDelete(item: SafetyConfirmsTableItem): void {
    this.$emit("onDelete", item);

    return;
  }
}

/** テーブルの列名の情報 */
export interface SafetyConfirmsTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface SafetyConfirmsTableItem {
  /** 施設ID */
  facilityId: number;
  /** 災害ID */
  id: number;
  /** 安否確認名 */
  name: string;
  /** 対応する otherUserMessages ソートキー */
  otherUserMessagesSortKey?: string[];
  /** 施設関係者にLINE送信したか否か */
  sendedOtherUserLine: boolean;
  /** 施設関係者にメール送信したか否か */
  sendedOtherUserMail: boolean;
  /** 施設関係者からの安否確認返信を受け付けるか */
  acceptingOtherUserReply: boolean;
  /** 対応する messages ソートキーを保持する */
  messagesSortKey?: Array<string>;
  /** 家族からの安否確認返信を受け付けるか */
  acceptingFamilyReply: boolean;
  /** 登録ユーザー ID */
  createUserId: string;
  /** 登録ユーザー名 */
  createUserName: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}
</script>

<style lang="scss" scoped>
.safety-confirm-table-component {
  .accepting-text,
  .no-accepting-text {
    width: 72px;
  }

  .register-btn {
    width: 120px;
  }

  .detail-btn,
  .delete-btn {
    width: 64px;
  }
}
</style>
